import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Company } from 'src/app/core/interfaces/company';
import { InsuranceInformation } from 'src/app/core/interfaces/insuranceInformation';
import { SaasTransportCompany } from 'src/app/core/interfaces/saasTransportCompany';
import { DateManager } from 'src/app/core/managers/date.manager';
import { AuthService } from './authentication.service';
import { SaasTransportCompanyService } from './saas-transport-company.service';

export type PolicyOwnerType = 'client' | 'transportCompany' | 'saasCompany';

export interface ResolvedPolicy {
  /** Póliza vigente, o null si el dueño resuelto no tiene uno registrado. */
  policy: InsuranceInformation;
  /** Quién aporta la póliza. null solo cuando no hay transportadora resoluble. */
  ownerType: PolicyOwnerType;
  transportCompany: SaasTransportCompany;
  expired: boolean;
}

/**
 * Única fuente de verdad sobre qué póliza rige un servicio. Convive con dos
 * regímenes, y el que aplica lo decide SaasTransportCompanyService.isEnabled:
 *
 * - SaaS con servicios de transporte: la bandera vive en la empresa de
 *   transporte asignada al contratante. policyByCompany true -> póliza del
 *   contratante; false/ausente -> póliza de la transportadora.
 * - SaaS sin servicios de transporte (escolta, candado satelital): no hay
 *   transportadoras, así que rige el régimen previo, con la bandera y la
 *   póliza en la propia empresa SaaS.
 */
@Injectable({ providedIn: 'root' })
export class InsurancePolicyService {

  constructor(
    private saasTransportCompanyService: SaasTransportCompanyService,
    private authService: AuthService
  ) { }

  // whenReady: al recargar la página isEnabled da false hasta que llega la
  // configuración SaaS; decidir el régimen antes caería en el legacy por error.
  resolve(client: Company, transportCompanyId: string): Observable<ResolvedPolicy> {
    return this.saasTransportCompanyService.whenReady().pipe(
      switchMap(() => {
        if (!this.saasTransportCompanyService.isEnabled) {
          return of(this.buildLegacyResolvedPolicy(client));
        }
        return this.saasTransportCompanyService.getById(transportCompanyId).pipe(
          map(transportCompany => this.buildResolvedPolicy(client, transportCompany))
        );
      })
    );
  }

  /** true cuando el contratante debe registrar su propia póliza. */
  clientPolicyRequired(transportCompanyId: string): Observable<boolean> {
    return this.saasTransportCompanyService.whenReady().pipe(
      switchMap(() => {
        if (!this.saasTransportCompanyService.isEnabled) {
          return of(this.appliesClientPolicyLegacy());
        }
        return this.saasTransportCompanyService.getById(transportCompanyId).pipe(
          map(transportCompany => this.appliesClientPolicy(transportCompany))
        );
      })
    );
  }

  /**
   * undefined, null y false son equivalentes: rige la póliza de la
   * transportadora. Solo un true explícito devuelve la del contratante.
   */
  appliesClientPolicy(transportCompany: SaasTransportCompany): boolean {
    return !!transportCompany && transportCompany.policyByCompany === true;
  }

  /**
   * Criterio del régimen previo: la bandera está en la empresa SaaS. Mismo
   * trato para undefined, null y false que en appliesClientPolicy.
   */
  appliesClientPolicyLegacy(): boolean {
    const saasCompany = this.authService && this.authService.getCompanySaaS();
    return !!saasCompany && saasCompany.policyByCompany === true;
  }

  /**
   * DD/MM/YYYY es el único formato de expirationDate en el sistema: lo
   * escriben tanto form-company (contratante) como el diálogo de póliza de
   * transportadora. Devuelve null si el valor no encaja.
   */
  parseExpirationDate(value: string): Date {
    if (!value) { return null; }
    return DateManager.stringToDate(value, 'DD/MM/YYYY');
  }

  isExpired(insurance: InsuranceInformation): boolean {
    if (!insurance || !insurance.expirationDate) { return false; }
    const expirationDate = this.parseExpirationDate(insurance.expirationDate);
    if (!expirationDate) { return false; }
    return DateManager.setStartOfDay(expirationDate).getTime()
      <= DateManager.setStartOfDay(new Date()).getTime();
  }

  private buildResolvedPolicy(client: Company, transportCompany: SaasTransportCompany): ResolvedPolicy {
    if (!transportCompany) {
      return { policy: null, ownerType: null, transportCompany: null, expired: false };
    }
    const ownerType: PolicyOwnerType = this.appliesClientPolicy(transportCompany)
      ? 'client'
      : 'transportCompany';
    const source = ownerType === 'client' ? client : transportCompany;
    return this.buildFromSource(source, ownerType, transportCompany);
  }

  /**
   * Régimen previo a las transportadoras por SaaS. No hay transportadora que
   * resolver, así que ownerType nunca es null: o la póliza es del contratante
   * o es la de la empresa SaaS.
   */
  private buildLegacyResolvedPolicy(client: Company): ResolvedPolicy {
    const ownerType: PolicyOwnerType = this.appliesClientPolicyLegacy() ? 'client' : 'saasCompany';
    const source = ownerType === 'client'
      ? client
      : this.authService && this.authService.getCompanySaaS();
    return this.buildFromSource(source, ownerType, null);
  }

  private buildFromSource(
    source: Company | SaasTransportCompany,
    ownerType: PolicyOwnerType,
    transportCompany: SaasTransportCompany
  ): ResolvedPolicy {
    const insurance = source && source.insuranceInformation ? source.insuranceInformation : null;
    const policy = insurance && insurance.policyNumber ? insurance : null;
    return { policy, ownerType, transportCompany, expired: this.isExpired(policy) };
  }
}
